import { useMemo } from 'react';

import { useAppSelector } from 'store/hooks';

import {
  getDimensionalPrecision,
  getDisplayUnits,
  getIsSceneUnitless,
} from '../data/reducer';
import { resolveSceneUnits } from '../session/sceneUnits';
import {
  type MeasurementFormat,
  measurementFormat,
} from '../utils/formatMeasurement';

/**
 * The conversion every displayed measurement goes through.
 *
 * Joins the two halves of unit state: `PartFile.dxfSceneState.units` supplies the
 * drawing's resolved `$INSUNITS`, redux supplies the user's preference and
 * precision. Measure, details, OBB, and axis dimensions all share this format.
 */
export const useDxfMeasurementFormat = (
  sceneUnits: number | null,
): MeasurementFormat => {
  const displayUnits = useAppSelector(getDisplayUnits);
  const precision = useAppSelector(getDimensionalPrecision);
  const isSceneUnitless = useAppSelector(getIsSceneUnitless);

  return useMemo(() => {
    const native = resolveSceneUnits(sceneUnits ?? 0);
    return measurementFormat({
      nativeUnits: native.displayUnits,
      nativeScale: native.scaleToDisplayUnits,
      // The store is authoritative once units have been published; falling back
      // to the freshly-resolved value covers the frame before the dispatch lands.
      isUnitless: isSceneUnitless || native.isUnitless,
      displayUnits,
      precision,
    });
  }, [sceneUnits, displayUnits, precision, isSceneUnitless]);
};
